import type { ReactNode } from 'react';
import { CircleHelp, FlaskConical } from 'lucide-react';

/**
 * Small notices for what the data does NOT say. Values from the kit.
 */

/**
 * Kit `OpenFlag` — a value the requirements leave [OPEN]. Render it INSTEAD of
 * a number, never alongside a placeholder. A fabricated tax threshold is worse
 * than a visible blank (handoff: "never substitute a placeholder value").
 */
export function OpenFlag({ children }: { children?: ReactNode }) {
  return (
    <span
      title="Unresolved in the requirements — do not implement against a placeholder value"
      className="inline-flex max-w-full items-start gap-1.5 rounded-[10px] bg-[rgba(180,158,49,.2)] px-2.5 py-1 font-body text-xs font-semibold leading-[1.35] text-[#7A5A12]"
    >
      <CircleHelp size={13} strokeWidth={2} aria-hidden="true" className="mt-px shrink-0" />
      <span className="min-w-0">
        OPEN{children ? <span className="font-normal opacity-[.85]"> · {children}</span> : null}
      </span>
    </span>
  );
}

/** Marks a screen or panel served by an MSW mock (G4/G5, points, swag — FE plan §4.2). */
export function MockBadge({ children = 'Mock data' }: { children?: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-pill bg-[rgba(180,158,49,.2)] px-2.5 py-[3px] font-body text-xs font-semibold text-[#7A5A12]">
      <FlaskConical size={12} strokeWidth={2} aria-hidden="true" />
      {children}
    </span>
  );
}

/**
 * A panel whose data isn't built yet (the BE's `notImplemented` blocks, or a
 * 501): says so plainly instead of rendering empty or invented numbers.
 */
export function NotAvailable({ title, reason }: { title: ReactNode; reason?: ReactNode }) {
  return (
    <div className="rounded-app border border-dashed border-[rgba(143,68,32,.2)] bg-cream-tint px-4 py-5 text-center font-body">
      <div className="text-sm font-semibold text-ink">{title}</div>
      {reason && <div className="mt-1 text-xs text-muted">{reason}</div>}
    </div>
  );
}
